import React from 'react';
import type { AgentMetadata } from '../../types/agent.types';

interface DiscoveryAgentCardProps {
  agent: AgentMetadata;
  onClick: () => void;
  className?: string;
}

/**
 * DiscoveryAgentCard - Individual agent card in discovery hub
 * Part of MVP-UX-002: Agent Discovery Hub
 */
export function DiscoveryAgentCard({ agent, onClick, className = '' }: DiscoveryAgentCardProps) {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'intelligence': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'content': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'optimization': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'automation': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'analytics': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div 
      className={`
        bg-white dark:bg-gray-800 
        rounded-lg border border-gray-200 dark:border-gray-700 
        p-6 space-y-4
        hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600
        transition-all duration-200
        cursor-pointer
        group
        ${className}
      `}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
            {agent.name}
          </h3>
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-1 ${getCategoryColor(agent.category)}`}>
            {agent.category}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
        {agent.description}
      </p>

      {/* Capabilities */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Key Capabilities
        </h4>
        <div className="flex flex-wrap gap-1">
          {agent.capabilities.slice(0, 3).map((capability, index) => (
            <span 
              key={index}
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
            >
              {capability}
            </span>
          ))}
          {agent.capabilities.length > 3 && (
            <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
              +{agent.capabilities.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Footer - Task-focused CTA */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            agent.status === 'active' ? 'bg-green-500' : 
            agent.status === 'busy' ? 'bg-yellow-500' : 'bg-gray-500'
          }`} />
          <span className="text-gray-500 dark:text-gray-400">
            {agent.status === 'active' ? 'Ready' : agent.status === 'busy' ? 'In use' : 'Offline'}
          </span>
        </div>
        <div className="text-blue-600 dark:text-blue-400 font-medium group-hover:underline">
          Get Started →
        </div>
      </div>
    </div>
  );
}